"use strict";
// Feature: address-geolocation
//
// GeoLocation — canonical physical location. Independent of `Address`
// (a postal address is not a physical location; a physical location may
// have no postal address).
//
// Design ref: `.kiro/specs/address-geolocation/design.md`. Requirements 3.x,
// 4.x, 5.x, 6.x, 7.x, 8.x.
Object.defineProperty(exports, "__esModule", { value: true });
exports.regenerateIdentifiers = exports.validateCoordinate = void 0;
/**
 * Validate a candidate coordinate pair. Accepts `unknown` and narrows —
 * callers may pass API-supplied JSON directly.
 *
 * Valid iff:
 *   - both are finite numbers (not NaN, not ±Infinity),
 *   - latitude ∈ [-90, 90],
 *   - longitude ∈ [-180, 180].
 *
 * Pure. Never throws. Never mutates the input.
 */
function validateCoordinate(latitude, longitude) {
    const check = (v, field, min, max) => {
        if (typeof v !== "number") {
            return { ok: false, error: { kind: "not-a-number", field } };
        }
        if (!Number.isFinite(v)) {
            return { ok: false, error: { kind: "not-finite", field } };
        }
        if (v < min || v > max) {
            return { ok: false, error: { kind: "out-of-range", field, value: v } };
        }
        return { ok: true, value: v };
    };
    const lat = check(latitude, "latitude", -90, 90);
    if (!lat.ok)
        return { ok: false, error: lat.error };
    const lng = check(longitude, "longitude", -180, 180);
    if (!lng.ok)
        return { ok: false, error: lng.error };
    return { ok: true, latitude: lat.value, longitude: lng.value };
}
exports.validateCoordinate = validateCoordinate;
/**
 * Regenerate `LocationIdentifiers` from coordinates.
 *
 * Pure — depends only on the inputs and injected generators. When no
 * generator is configured for a given identifier, that field is left absent
 * on the result. Returns `undefined` if no generator is configured at all,
 * so callers can `if (identifiers) {...}` cleanly.
 *
 * Design invariant (Property 5): coordinates change → identifiers regenerated
 * from the new coordinates; identifiers change → coordinates unchanged.
 */
function regenerateIdentifiers(latitude, longitude, generators) {
    if (!generators)
        return undefined;
    const out = {};
    if (generators.what3words) {
        const v = generators.what3words(latitude, longitude);
        if (typeof v === "string" && v.length > 0)
            out.what3words = v;
    }
    if (generators.plusCode) {
        const v = generators.plusCode(latitude, longitude);
        if (typeof v === "string" && v.length > 0)
            out.plusCode = v;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
exports.regenerateIdentifiers = regenerateIdentifiers;
